import {
  formatDateTime,
  formatHumanizedDistance,
} from '@backoffice-frontend/common'
import { CopyToClipboardIconButton } from '@backoffice-frontend/patterns'
import {
  CheckCircleIcon,
  ExternalLinkIcon,
  MicrophoneIcon,
  VehicleIcon,
  XCircleIcon,
} from '@moia-dev/pace-icons'
import { Heading, Icon, Text } from '@moia-dev/pace-web'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AnnotationJiraLink } from './AnnotationJiraLink'
import { AnnotationResolvedBadge } from './AnnotationResolvedBadge'
import {
  ANNOTATION_RESOLVED_OPACITY,
  annotationSeverityColor,
  annotationSeverityLabel,
  annotationTranscriptPlaceholder,
} from './annotation-labels'
import { TripAnnotationAudioPlayer } from './TripAnnotationAudioPlayer'
import type { TripAnnotationMarker } from './types'

export type AnnotationPopupContentProps = {
  annotation: TripAnnotationMarker
  /**
   * Builds the href for the "open trip" link from the annotation's `tripId`.
   *
   * Injected rather than imported, because the target route belongs to the
   * consuming area — route-builder points it at its own trip editor, trip-history
   * at a trip detail — and a shared lib importing either area's route constants
   * would couple every consumer to whichever one it picked. Omit it (or return
   * null) where no trip route exists and the link is left out entirely rather
   * than rendered dead.
   */
  buildTripHref?: (tripId: string) => string | null
  /**
   * Extra rows the consuming area renders under the transcript, or nothing.
   *
   * A slot rather than a set of fields, for the same reason as `buildTripHref`:
   * what belongs there is the consumer's business. The annotation-triage page
   * puts the ADR-0029 classification there — category, who labelled it, banded
   * confidence — and those labels and their translations live with that page,
   * the only one that renders a classification. Route-builder and trip-history
   * pass nothing and the popup is unchanged for them.
   */
  renderDetails?: (annotation: TripAnnotationMarker) => ReactNode
}

/**
 * Width of the popup card. `useAnnotationMapLayer` hands the same number to
 * MapLibre as the popup's `maxWidth`, so MapLibre never squeezes the card
 * narrower than it was laid out for.
 */
export const ANNOTATION_POPUP_WIDTH_PX = 340

const SEVERITY_AVATAR_SIZE = 40

/**
 * One band of the popup, padded on its own.
 *
 * The popup shell has no padding (see `ANNOTATION_POPUP_CLASS`), so the dividers
 * between sections can run edge to edge. Each section brings its own padding
 * back instead.
 */
const PopupSection = ({ children }: { children: ReactNode }) => (
  <div
    css={theme => ({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.space.Space3,
      padding: theme.space.Space4,
      minWidth: 0,
    })}
  >
    {children}
  </div>
)

const PopupDivider = () => (
  <div
    css={theme => ({
      borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
    })}
  />
)

/** A section's title on the left and, optionally, something on the far right. */
const SectionHeading = ({
  title,
  adornment,
  trailing,
}: {
  title: string
  adornment?: ReactNode
  trailing?: ReactNode
}) => (
  <div
    css={theme => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.space.Space2,
      minHeight: '24px',
    })}
  >
    {/* The title gets its own element, so the decorative adornment beside it
        is not part of its text. */}
    <Text size="md" weight="bold">
      <span>{title}</span>
      {adornment}
    </Text>
    {trailing}
  </div>
)

/** A 20px dark icon in front of one line of text. */
const IconLine = ({
  icon,
  children,
}: {
  icon: ReactNode
  children: ReactNode
}) => (
  <div
    css={theme => ({
      display: 'flex',
      alignItems: 'center',
      gap: theme.space.Space2,
      minWidth: 0,
    })}
  >
    <Icon
      size={20}
      css={theme => ({
        color: theme.semantic.ColorContentDefault,
        flexShrink: 0,
      })}
    >
      {icon}
    </Icon>
    {children}
  </div>
)

/**
 * The ✗/✓ pair on the far right of the transcript heading, drawn as in the
 * design: a red ✗ and a green ✓, both always in full colour.
 *
 * Display only. A resolution needs evidence (a Jira issue, a release, a map
 * version), so a one-click tick could never create one; the "Resolved" tag
 * under the header is what marks a fixed note. Since the pair looks the same
 * either way, it is a single image to assistive tech, named for the note's
 * actual resolution state, so a screen reader still hears whether it was fixed.
 */
const ResolutionIndicator = ({ resolved }: { resolved: boolean }) => {
  const { t } = useTranslation()
  return (
    <span
      role="img"
      aria-label={
        resolved
          ? t('mapAnnotations_resolution_badge')
          : t('mapAnnotations_resolution_open')
      }
      css={theme => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.space.Space3,
        flexShrink: 0,
      })}
    >
      <Icon
        size={20}
        css={theme => ({ color: theme.semantic.ColorCriticalDefault })}
      >
        <XCircleIcon />
      </Icon>
      <Icon
        size={20}
        css={theme => ({ color: theme.semantic.ColorSuccessDefault })}
      >
        <CheckCircleIcon />
      </Icon>
    </span>
  )
}

/**
 * Hover popup for one driver voice-note marker. Portaled in from
 * `useAnnotationMapLayer` (see its `annotationPopups` render output) rather
 * than mounted as a second React root, so this has the app's real
 * theme/i18n/router context exactly like any other component -- see that
 * file's comments for why a detached `createRoot` wouldn't.
 *
 * Three bands, top to bottom: who and when (with the trip and the resolution),
 * what the driver said, and the id to copy.
 */
export const AnnotationPopupContent = ({
  annotation,
  buildTripHref,
  renderDetails,
}: AnnotationPopupContentProps) => {
  const { t } = useTranslation()
  const color = annotationSeverityColor(annotation.severity)
  const recordedAt = new Date(annotation.recordedAtMs)
  const tripHref =
    annotation.tripId && buildTripHref ? buildTripHref(annotation.tripId) : null

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        width: `${ANNOTATION_POPUP_WIDTH_PX}px`,
        maxWidth: '100%',
      }}
    >
      <PopupSection>
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.space.Space3,
          })}
        >
          {/*
            Same colour as the marker, and faded the same way once resolved, so
            the popup and the pin it came from read as one thing. Decorative:
            the severity is spelled out in the title beside it. The Icon sets
            its own colour, so the white has to go on the Icon, not the circle.
          */}
          <span
            aria-hidden
            css={theme => ({
              width: SEVERITY_AVATAR_SIZE,
              height: SEVERITY_AVATAR_SIZE,
              borderRadius: theme.radius.RadiusFull,
              backgroundColor: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              opacity: annotation.resolution ? ANNOTATION_RESOLVED_OPACITY : 1,
            })}
          >
            <Icon size={20} css={{ color: 'white' }}>
              <MicrophoneIcon />
            </Icon>
          </span>

          <div
            css={theme => ({
              display: 'flex',
              flexDirection: 'column',
              gap: theme.space.Space1,
              flex: 1,
              minWidth: 0,
            })}
          >
            <div
              css={theme => ({
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: theme.space.Space3,
              })}
            >
              <Heading size="lg" css={{ margin: 0, overflowWrap: 'anywhere' }}>
                {t('mapAnnotations_popup_title', {
                  severity: annotationSeverityLabel(t, annotation.severity),
                })}
              </Heading>
              <Text
                size="md"
                color="reduced"
                css={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {formatHumanizedDistance(recordedAt.toISOString())}
              </Text>
            </div>
            <Text size="md">{formatDateTime(recordedAt)}</Text>
            {annotation.vehicleId ? (
              <IconLine icon={<VehicleIcon />}>
                <Text size="md">{annotation.vehicleId}</Text>
              </IconLine>
            ) : null}
            {tripHref ? (
              <IconLine icon={<ExternalLinkIcon />}>
                <a
                  href={tripHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  css={theme => ({
                    color: theme.semantic.ColorAccentDefault,
                    textDecoration: 'none',
                  })}
                >
                  <Text size="md" css={{ color: 'inherit' }}>
                    {t('mapAnnotations_popup_openTrip')}
                  </Text>
                </a>
              </IconLine>
            ) : annotation.vtrId ? (
              // No VTR-specific route exists in this app -- VTRs are handled
              // inline via dialogs/selectors, not navigation -- so this is
              // informational text, not a link, unlike the tripId case above.
              <IconLine icon={<ExternalLinkIcon />}>
                <Text size="md" color="reduced">
                  {t('mapAnnotations_popup_vtrLabel', {
                    vtrId: annotation.vtrId,
                  })}
                </Text>
              </IconLine>
            ) : null}
            {annotation.jiraIssueKey ? (
              <IconLine icon={<ExternalLinkIcon />}>
                <AnnotationJiraLink issueKey={annotation.jiraIssueKey} />
              </IconLine>
            ) : null}
          </div>
        </div>

        {/*
          Only a resolved note gets a tag. An unresolved one says nothing rather
          than "Unresolved": that is the normal state of almost every note, and
          a tag on all of them would drown out the few that were fixed.
        */}
        {annotation.resolution ? (
          <AnnotationResolvedBadge resolution={annotation.resolution} />
        ) : null}
      </PopupSection>

      <PopupDivider />

      <PopupSection>
        <SectionHeading
          title={t('mapAnnotations_popup_transcriptHeading')}
          adornment={
            <span
              aria-hidden
              css={theme => ({
                color: theme.semantic.ColorCriticalDefault,
                marginLeft: theme.space.Space1,
              })}
            >
              *
            </span>
          }
          trailing={<ResolutionIndicator resolved={!!annotation.resolution} />}
        />
        <div
          css={theme => ({
            backgroundColor: theme.semantic.ColorInputDefault,
            border: `1px solid ${theme.semantic.ColorBorderOnInputDefault}`,
            borderRadius: theme.radius.RadiusMedium,
            padding: `${theme.space.Space3} ${theme.space.Space4}`,
            overflowWrap: 'anywhere',
          })}
        >
          {/*
            With no transcript, the placeholder names which kind of missing it
            is (pending, failed, …), so the heading carries no status of its
            own.
          */}
          {annotation.transcript ? (
            <Text size="md" color="reduced">
              {annotation.transcript}
            </Text>
          ) : (
            <Text size="md" color="reduced" css={{ fontStyle: 'italic' }}>
              {annotationTranscriptPlaceholder(
                t,
                annotation.transcriptionStatus,
              )}
            </Text>
          )}
        </div>

        {renderDetails?.(annotation)}

        {/* Keyed on the note so switching popups never carries one note's URL
            or state over to another. */}
        <TripAnnotationAudioPlayer
          key={annotation.id}
          annotationId={annotation.id}
        />
      </PopupSection>

      <PopupDivider />

      <PopupSection>
        {/*
          The id is deliberately not passed as `children` to the copy button:
          with no `variant` prop, CopyToClipboardIconButton's own CSS has no
          `gridTemplateColumns`, so a text child and its internal icon `<span>`
          land in separate implicit grid rows and stack vertically. The button
          stays icon-only, on the far right of the heading.

          The wrapper restyles the button's icon to the design's dark, unpadded
          20px glyph. Its selectors are one element more specific than the
          button's own `svg` / `span` rules, so they win without `!important`.
        */}
        <SectionHeading
          title={t('mapAnnotations_popup_idHeading')}
          trailing={
            <span
              css={theme => ({
                display: 'flex',
                flexShrink: 0,
                '& button span': { padding: 0, marginLeft: 0 },
                '& button svg': {
                  width: '20px',
                  height: '20px',
                  color: theme.semantic.ColorContentDefault,
                },
              })}
            >
              <CopyToClipboardIconButton
                content={annotation.id}
                aria-label={t('mapAnnotations_popup_copyIdLabel')}
              />
            </span>
          }
        />
        {/* One line, cut with an ellipsis; the copy button has the full id. */}
        <Text
          size="md"
          color="reduced"
          title={annotation.id}
          css={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {annotation.id}
        </Text>
      </PopupSection>
    </div>
  )
}
